import { Box, DataList, Flex, Table } from '@volue/wave-react';
import React from 'react';

import { EvaluatedResultComponent } from '@/features/execute-calculation-set/components/EvaluatedResultComponent';
import { EvaluatedValue } from '@/features/execute-calculation-set/components/MediumVoltageCalculation/LoadFlowSummaryCalculationResults/EvaluatedCalculationResult';
import type { SelectedGridElement } from '@/features/execute-calculation-set/overmind/calculation';
import type { PowerTransformerShortCircuitDetails } from '@/features/execute-calculation-set/overmind/calculation/derived-state/gridElementsDetailsByCalculationType/shortCircuit/ShortCircuitDetails';
import type { ElectricCurrentType } from '@/features/execute-calculation-set/overmind/calculation/domain/ElectricCurrent';
import type { EvaluatedResult } from '@/features/execute-calculation-set/overmind/calculation/evaluation/EvaluatedResult';

type SelectedPowerTransformer = {
  type: 'SELECTED_GRID_ELEMENT';
  gridElement: SelectedGridElement<'PowerTransformer'>;
};

export const PowerTransformerDetails: React.FunctionComponent<{
  powerTransformer: SelectedPowerTransformer;
}> = ({ powerTransformer }) => {
  return (
    <>
      {powerTransformer.gridElement.loadflow.type ===
        'ALL_LOAD_FLOW_SCENARIO_DETAILS' && (
        <Flex flow="column" gap={'spacingXs'}>
          <Box css={{ color: '$colorAccent70', fontWeight: 'bolder' }}>
            {'LoadFlow'}
          </Box>
          <Table aria-label="Load scenarios" highlightOnHover={false}>
            <Table.Header>
              <Table.ColumnHeader>{'Scenario'}</Table.ColumnHeader>
              <>
                {powerTransformer.gridElement.loadflow.details.map(scenario => (
                  <Table.ColumnHeader>
                    {scenario.scenarioName}
                  </Table.ColumnHeader>
                ))}
              </>
            </Table.Header>
            <Table.Body>
              <Table.Row>
                <Flex
                  as={Table.Cell}
                  flow="column"
                  gap={'none'}
                  justifyContent="start"
                  alignItems="start"
                >
                  <Box>Primary Voltage</Box>
                </Flex>
                <>
                  {powerTransformer.gridElement.loadflow.details.map(
                    scenario => (
                      <Table.Cell>
                        {scenario.scenarioDetails.primaryVoltage.toString()}
                      </Table.Cell>
                    )
                  )}
                </>
              </Table.Row>
              <Table.Row>
                <Flex
                  as={Table.Cell}
                  flow="column"
                  gap={'none'}
                  justifyContent="start"
                  alignItems="start"
                >
                  <Box>Secondary Voltage</Box>
                </Flex>
                <>
                  {powerTransformer.gridElement.loadflow.details.map(
                    scenario => (
                      <Table.Cell>
                        {scenario.scenarioDetails.secondaryVoltage.toString()}
                      </Table.Cell>
                    )
                  )}
                </>
              </Table.Row>
              {powerTransformer.gridElement.loadflow.details.every(
                scenario =>
                  scenario.scenarioDetails.powerTransfomerType ===
                  'ThreeWinding'
              ) && (
                <Table.Row>
                  <Flex
                    as={Table.Cell}
                    flow="column"
                    gap={'none'}
                    justifyContent="start"
                    alignItems="start"
                  >
                    <Box>Tertiary Voltage</Box>
                  </Flex>
                  <>
                    {powerTransformer.gridElement.loadflow.details.map(
                      scenario =>
                        scenario.scenarioDetails.powerTransfomerType ===
                          'ThreeWinding' && (
                          <Table.Cell>
                            {scenario.scenarioDetails.tertiaryVoltage.toString()}
                          </Table.Cell>
                        )
                    )}
                  </>
                </Table.Row>
              )}
              <Table.Row>
                <Flex
                  as={Table.Cell}
                  flow="column"
                  gap={'none'}
                  justifyContent="start"
                  alignItems="start"
                >
                  <Box>Rated Capacity</Box>
                </Flex>
                <>
                  {powerTransformer.gridElement.loadflow.details.map(
                    scenario => (
                      <Table.Cell>
                        <EvaluatedResultComponent
                          result={
                            scenario.scenarioDetails.results.ratedCapacity
                          }
                        />
                      </Table.Cell>
                    )
                  )}
                </>
              </Table.Row>
              <Table.Row>
                <Flex
                  as={Table.Cell}
                  flow="column"
                  gap={'none'}
                  justifyContent="start"
                  alignItems="start"
                >
                  <Box>Loading</Box>
                </Flex>
                <>
                  {powerTransformer.gridElement.loadflow.details.map(
                    scenario => (
                      <Table.Cell>
                        <EvaluatedResultComponent
                          result={
                            scenario.scenarioDetails.results.loadingPercent
                          }
                        />
                      </Table.Cell>
                    )
                  )}
                </>
              </Table.Row>
              <Table.Row>
                <Flex
                  as={Table.Cell}
                  flow="column"
                  gap={'none'}
                  justifyContent="start"
                  alignItems="start"
                >
                  <Box>Available Capacity</Box>
                </Flex>
                <>
                  {powerTransformer.gridElement.loadflow.details.map(
                    scenario => (
                      <Table.Cell>
                        <EvaluatedResultComponent
                          result={
                            scenario.scenarioDetails.results.availableCapacity
                          }
                        />
                      </Table.Cell>
                    )
                  )}
                </>
              </Table.Row>
              <Table.Row>
                <Flex
                  as={Table.Cell}
                  flow="column"
                  gap={'none'}
                  justifyContent="start"
                  alignItems="start"
                >
                  <Box>Sum Active Power</Box>
                </Flex>
                <>
                  {powerTransformer.gridElement.loadflow.details.map(
                    scenario => (
                      <Table.Cell>
                        {scenario.scenarioDetails.sumActivePower.toString()}
                      </Table.Cell>
                    )
                  )}
                </>
              </Table.Row>
              <Table.Row>
                <Flex
                  as={Table.Cell}
                  flow="column"
                  gap={'none'}
                  justifyContent="start"
                  alignItems="start"
                >
                  <Box>Sum Reactive Power</Box>
                </Flex>
                <>
                  {powerTransformer.gridElement.loadflow.details.map(
                    scenario => (
                      <Table.Cell>
                        {scenario.scenarioDetails.sumReactivePower.toString()}
                      </Table.Cell>
                    )
                  )}
                </>
              </Table.Row>
            </Table.Body>
          </Table>
        </Flex>
      )}
      {powerTransformer.gridElement.shortCircuit.type ===
        'SHORT_CIRCUIT_DETAILS' && (
        <Flex flow="column" gap={'spacingXs'}>
          <Box css={{ color: '$colorAccent70', fontWeight: 'bolder' }}>
            {'Short Circuit'}
          </Box>

          {powerTransformer.gridElement.objectType === 'PowerTransformer' &&
            powerTransformer.gridElement.shortCircuit.evaluatedResults
              .objectType === 'PowerTransformer' && (
              <DataList className="mHxl" ratio={[3, 1]}>
                <Phase1Min
                  results={
                    powerTransformer.gridElement.shortCircuit.evaluatedResults
                      .results
                  }
                />
                <Phase1Max
                  results={
                    powerTransformer.gridElement.shortCircuit.evaluatedResults
                      .results
                  }
                />
                <Phase2Min
                  results={
                    powerTransformer.gridElement.shortCircuit.evaluatedResults
                      .results
                  }
                />
                <Phase3Max
                  results={
                    powerTransformer.gridElement.shortCircuit.evaluatedResults
                      .results
                  }
                />
              </DataList>
            )}
        </Flex>
      )}
    </>
  );
};

function EvaluatedDataListItem({
  label,
  value
}: {
  label: string;
  value: EvaluatedResult<ElectricCurrentType>;
}): JSX.Element | null {
  return (
    <Flex as={DataList.Row} main="space-between">
      <DataList.Label>{label}</DataList.Label>
      <Flex main={'end'} as={DataList.Value}>
        <EvaluatedValue value={value.value.toString()} status={value.status} />
      </Flex>
    </Flex>
  );
}

function Phase1Min({
  results
}: {
  results: PowerTransformerShortCircuitDetails['results'];
}): JSX.Element | null {
  return results.phase1Min.type === 'CALCULATION_RESULT_AVAILABLE' ? (
    <EvaluatedDataListItem label="Phase1Min" value={results.phase1Min.result} />
  ) : null;
}

function Phase1Max({
  results
}: {
  results: PowerTransformerShortCircuitDetails['results'];
}): JSX.Element | null {
  return results.phase1Max.type === 'CALCULATION_RESULT_AVAILABLE' ? (
    <EvaluatedDataListItem label="Phase1Max" value={results.phase1Max.result} />
  ) : null;
}

function Phase2Min({
  results
}: {
  results: PowerTransformerShortCircuitDetails['results'];
}): JSX.Element | null {
  return results.phase2Min.type === 'CALCULATION_RESULT_AVAILABLE' ? (
    <EvaluatedDataListItem label="Phase2Min" value={results.phase2Min.result} />
  ) : null;
}

function Phase3Max({
  results
}: {
  results: PowerTransformerShortCircuitDetails['results'];
}): JSX.Element | null {
  return results.phase3Max.type === 'CALCULATION_RESULT_AVAILABLE' ? (
    <EvaluatedDataListItem label="Phase3Max" value={results.phase3Max.result} />
  ) : null;
}
